/**
 * sidebarNewThreadRoute — the `/new` route target for the sidebar header's
 * "New thread" action.
 *
 * ── the failure this exists to prevent (CMMD-2266) ──────────────────────────
 *
 * This action used to navigate to a bare `/new`, discarding the one thing only
 * the click site knows: which project the user is currently in. The landing
 * then fell back to `resolveDefaultProject(..., projectOrder)`, and
 * `projectOrder` is the persisted sidebar DRAG arrangement, not a recency
 * signal — so `/new` always opened on whichever project sat first in the
 * sidebar.
 *
 * That is not a cosmetic default. The resolved project decides which runtime
 * `useForgeProviderAccess` queries, so landing on the wrong project repoints
 * the model picker at a different runtime's provider inventory and falls back
 * to `"codex"` when that runtime publishes nothing. Users experienced it as the
 * provider silently changing to Claude or Codex.
 *
 * Carrying the project in the URL keeps the intent explicit, shareable, and
 * survivable across reload. It is deliberately belt-and-braces with the
 * landing's own active-project default: if this preselect ever fails to match a
 * picker row, `NewThreadLanding`'s effect leaves the default resolution
 * untouched rather than rendering an empty selection.
 */
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";

/**
 * The `execution` query param value on `/new` (route: apps/web/src/routes/_chat.new.tsx).
 *
 * Must stay equal to `CLOUD_EXECUTION_ID` in `../newThread/NewThreadLanding.logic.ts`,
 * which is what the composer matches against. It is duplicated rather than imported
 * on purpose: `newThread/` already imports from `sidebar/`, so importing back the
 * other way would close a circular dependency between the two directories. If the
 * two ever diverge the preselect silently falls back to the default execution target.
 *
 * Deliberately NOT exported — file-local use only, same as the twin constant in
 * `preConnectCloudNewThreadRoute.ts`: the dead-code gate flags an unused export,
 * and there is no second consumer.
 */
const CLOUD_EXECUTION_ID = "cloud";

export interface SidebarNewThreadProjectRef {
  readonly environmentId: string;
  readonly projectId: string;
}

export interface SidebarNewThreadRoute {
  readonly to: "/new";
  readonly search?: { readonly project: string; readonly execution: string };
}

/**
 * Resolve the `/new` navigation target, preselecting `activeProjectRef` when the
 * user is currently working inside a project.
 *
 * Cloud projects are addressed by the `"cloud"` execution bucket rather than by
 * their literal environment id, because that is how `filterProjectsForExecution`
 * partitions the picker; a cmmd-runtime environment id passed through verbatim
 * would filter the very project it names out of the list.
 */
export function resolveSidebarNewThreadRoute(
  activeProjectRef: SidebarNewThreadProjectRef | null,
): SidebarNewThreadRoute {
  if (!activeProjectRef) return { to: "/new" };
  return {
    to: "/new",
    search: {
      project: activeProjectRef.projectId,
      execution: classifyEnvironmentId(activeProjectRef.environmentId).isCloud
        ? CLOUD_EXECUTION_ID
        : activeProjectRef.environmentId,
    },
  };
}
